import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

const FormatRow = ({ f, onSaved }) => {
  const [val, setVal] = useState(f.resale_per_sqft ?? "");
  const [warn, setWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(f.resale_per_sqft ?? ""), [f.resale_per_sqft]);
  const n = parseFloat(val);
  const box = !isNaN(n) && f.sqft_per_box ? (n * f.sqft_per_box).toFixed(2) : null;
  const liveNeg = !isNaN(n) && f.wholesale_cost_per_sqft_max != null && n < f.wholesale_cost_per_sqft_max;

  const save = async (ack = false) => {
    if (isNaN(n)) return;
    setBusy(true);
    try {
      await api.put(`/dealer/pricing/format-defaults/${f.format_code}`, { resale_per_sqft: n, acknowledge_below_cost: ack });
      setWarn(null);
      toast.success(`${f.label}: ${money(n)}/sq ft saved for all ${f.variant_count} colors`);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "below_cost") setWarn(d); else toast.error(formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-white p-4" data-testid={`format-default-${f.format_code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium text-charcoal">{f.label} {f.resale_per_sqft != null && <CheckCircle2 className="ml-1 inline h-4 w-4 text-sage" data-testid={`format-default-done-${f.format_code}`} />}</p>
          <p className="text-xs text-muted-foreground">{f.variant_count} colors · {f.sqft_per_box} sq ft per box · your wholesale cost {money(f.wholesale_cost_per_sqft_min)}{f.wholesale_cost_per_sqft_max !== f.wholesale_cost_per_sqft_min ? `–${money(f.wholesale_cost_per_sqft_max)}` : ""}/sq ft</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">$</span>
            <Input type="number" step="0.01" min="0" value={val} onChange={(e) => { setVal(e.target.value); setWarn(null); }} className="h-10 w-28 bg-white" data-testid={`format-default-input-${f.format_code}`} aria-label={`${f.label} selling price per square foot`} />
            <span className="text-xs text-muted-foreground">/sq ft</span>
            <Button type="submit" disabled={busy || isNaN(n)} className="bg-terracotta text-white hover:bg-terracotta-hover" data-testid={`format-default-save-${f.format_code}`}>Save</Button>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid={`format-default-box-${f.format_code}`}>= {box ? `$${box}` : "$0.00"}/box{liveNeg ? " · below wholesale cost" : ""}</p>
        </form>
      </div>
      {warn && (
        <div className="mt-3 rounded-md border border-terracotta/50 bg-terracotta/5 p-3 text-sm" data-testid={`below-cost-warning-${f.format_code}`}>
          <p className="flex items-center gap-2 font-medium text-terracotta"><AlertTriangle className="h-4 w-4" /> Negative gross margin</p>
          <p className="mt-1 text-xs text-charcoal">Wholesale cost {money(warn.wholesale_cost_per_sqft)}/sq ft · your price {money(warn.proposed_per_sqft)}/sq ft · gross profit {money(warn.gross_profit_per_sqft)}/sq ft · margin {warn.margin_pct}%</p>
          <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => save(true)} data-testid={`below-cost-ack-${f.format_code}`}>I understand — keep this price</Button>
        </div>
      )}
    </div>
  );
};

export default function PricingSetup() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/dealer/pricing/format-defaults").then((r) => { setData(r.data); window.dispatchEvent(new Event("dealer:pricing-changed")); }), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="mx-auto max-w-3xl" data-testid="pricing-setup-page">
      <p className="text-[11px] uppercase tracking-[0.18em] text-warmgrey">Onboarding</p>
      <h1 className="mt-2 font-display text-4xl font-light text-charcoal">Set up your selling prices</h1>
      <p className="mt-2 text-sm text-muted-foreground">Enter the price you charge your customers per square foot for each format. Zelligio Connect applies it to every color in that format and automatically calculates the selling price per box. You can refine individual products or projects later in Pricing.</p>
      <div className="mt-6 space-y-3">{data.formats.map((f) => <FormatRow key={f.format_code} f={f} onSaved={load} />)}</div>
      <div className="mt-6 flex items-center justify-between rounded-lg border border-border bg-limestone/40 p-4" data-testid="pricing-setup-status">
        <p className="text-sm text-charcoal">{data.pricing_setup_complete ? "All three formats are priced — your portal is unlocked." : `${data.formats.filter((f) => f.resale_per_sqft != null).length} of 3 formats priced.`}</p>
        {data.pricing_setup_complete && <Button onClick={() => navigate("/dealer")} data-testid="pricing-setup-continue" className="bg-charcoal text-white">Continue to your dashboard</Button>}
      </div>
    </div>
  );
}
